import Dividers from "../Dividers/Dividers";
import Fraction from "../Fraction/Fraction";

class PolynomialFormula {
    private coefficients: number[];
    private dividersa0string: string = "";
    private dividersa0: number[] = [];
    private dividersa0WithMinuses: number[] = [];
    private dividersanstring: string = "";
    private dividersan: number[] = [];
    private dividersanWithMinuses: number[] = [];

    constructor(coefficients: number[]) {
        if (coefficients.length < 2 || coefficients.length > 10) {
            throw new Error("Number of coefficients must be between 2 and 10.");
        }
        this.coefficients = coefficients;
        this.setDividersA0SetWithMinusesString();
        this.setDividersAnSetWithMinusesString();
    }
    private setDividersA0SetWithMinusesString(): void {
        const constantTerm = Math.abs(this.coefficients[this.coefficients.length - 1]);
        this.dividersa0string = new Dividers(constantTerm).getDividersSetWithMinusesString();
        this.dividersa0 = new Dividers(constantTerm).getDividers();
        this.dividersa0WithMinuses = new Dividers(constantTerm).getDividersWithMinuses();
    }
    public getDividersA0SetWithMinusesString(): string {
        return this.dividersa0string;
    }
    public getDividersA0(): number[] {
        return this.dividersa0;
    }
    public getDividersA0WithMinuses(): number[] {
        return this.dividersa0WithMinuses;
    }
    private setDividersAnSetWithMinusesString(): void {
        const constantTerm = Math.abs(this.coefficients[0]);
        this.dividersanstring = new Dividers(constantTerm).getDividersSetWithMinusesString();
        this.dividersan = new Dividers(constantTerm).getDividers();
        this.dividersanWithMinuses = new Dividers(constantTerm).getDividersWithMinuses();
    }
    public getDividersAnSetWithMinusesString(): string {
        return this.dividersanstring;
    }
    public getDividersAn(): number[] {
        return this.dividersan;
    }
    public getDividersAnWithMinuses(): number[] {
        return this.dividersanWithMinuses;
    }
    public getAllPossibleRationalRootsAsReducedStrings(): string[] {
        const numerators = this.getDividersA0(); // p
        const denominators = this.getDividersAn(); // q

        const fractionsMap = new Map<string, number>();

        numerators.forEach((p) => {
            denominators.forEach((q) => {
                try {
                    const positiveFraction = new Fraction(p, q);
                    const negativeFraction = new Fraction(-p, q);

                    fractionsMap.set(
                        positiveFraction.getFractionReducedString(),
                        positiveFraction.getValue()
                    );
                    fractionsMap.set(
                        negativeFraction.getFractionReducedString(),
                        negativeFraction.getValue()
                    );
                } catch (e) {
                    //dopisz dzielenie przez zero
                }
            });
        });

        const entries = Array.from(fractionsMap.entries());

        const negatives = entries
            .filter(([_, value]) => value < 0)
            .sort((a, b) => a[1] - b[1]);

        const positives = entries
            .filter(([_, value]) => value > 0)
            .sort((a, b) => a[1] - b[1]);

        return [...negatives, ...positives].map(([fractionStr]) => fractionStr);
    }
    public toLatex(): string {
        const degree = this.coefficients.length - 1;

        const terms = this.coefficients.map((coef, idx) => {
            const power = degree - idx;

            if (coef === 0) return "";

            const absCoef = Math.abs(coef);
            const sign = coef > 0 ? "+" : "-";

            let coefStr = "";

            if (absCoef === 1 && power !== 0) {
                coefStr = "";
            } else {
                coefStr = absCoef.toString();
            }

            let variableStr = "";
            if (power > 1) {
                variableStr = `x^{${power}}`;
            } else if (power === 1) {
                variableStr = "x";
            }

            return `${sign} ${coefStr}${variableStr}`.trim();
        });

        let latex = terms.filter(t => t !== "").join(" ");
        if (latex.startsWith("+")) {
            latex = latex.slice(2); // remove '+ '
        }

        return latex;
    }
    public evaluateAtDividersWithMinuses(): string[] {
        const constantTerm = Math.abs(this.coefficients[this.coefficients.length - 1]);
        const dividers = new Dividers(constantTerm);
        const dividersWithMinuses = dividers.getDividersWithMinuses();

        return dividersWithMinuses.map((x) => {
            const formattedX = x < 0 ? `(${x})` : `${x}`;

            const termsWithValues = this.coefficients.map((coef, index) => {
                const power = this.coefficients.length - 1 - index;

                const formattedCoef = coef < 0 ? `(${coef})` : `${coef}`;
                let term = "";

                if (power === 0) {
                    term = `${formattedCoef}`;
                } else if (power === 1) {
                    term = `${formattedCoef}·${formattedX}`;
                } else {
                    term = `${formattedCoef}·${formattedX}^${power}`;
                }

                return term;
            });

            const value = this.coefficients.reduce((acc, coef, index) => {
                const power = this.coefficients.length - 1 - index;
                return acc + coef * Math.pow(x, power);
            }, 0);

            const expression = termsWithValues.join(" + ").replace(/\+\s-\(/g, "- (");
            return `W(${x}) = ${expression} = ${value}`;
        });
    }
    public evaluateAtAllRationalRoots(): string[] {
        const numerators = this.getDividersA0(); // p
        const denominators = this.getDividersAn(); // q

        const results: string[] = [];
        const seen = new Set<string>();

        numerators.forEach((p) => {
            denominators.forEach((q) => {
                try {
                    const positive = new Fraction(p, q);
                    const negative = new Fraction(-p, q);

                    [positive, negative].forEach((fraction) => {
                        const fracStr = fraction.getFractionReducedString();
                        if (seen.has(fracStr)) return;
                        seen.add(fracStr);

                        const x = fraction.getValue();
                        const formattedX = x < 0 ? `\\left(${fracStr}\\right)` : fracStr;
                        const isFraction = fracStr.includes("frac");

                        const terms = this.coefficients.map((coef, index) => {
                            const power = this.coefficients.length - 1 - index;
                            const formattedCoef = coef < 0 ? `(${coef})` : `${coef}`;
                            const base = isFraction && power >= 2 ? `\\left(${fracStr}\\right)` : formattedX;

                            if (power === 0) return `${formattedCoef}`;
                            if (power === 1) return `${formattedCoef}·${formattedX}`;
                            return `${formattedCoef}·${base}^{${power}}`;
                        });

                        const rawValue = this.coefficients.reduce((acc, coef, index) => {
                            const power = this.coefficients.length - 1 - index;
                            return acc + coef * Math.pow(x, power);
                        }, 0);

                        const roundedValue = Math.round(rawValue * 100) / 100;

                        const isExact = Math.abs(rawValue - roundedValue) < 1e-10;
                        const approxSymbol = isExact ? "=" : "\\approx";

                        const expression = terms.join(" + ").replace(/\+\s-\(/g, "- (");

                        results.push(`W\\left( ${fracStr} \\right) = ${expression} ${approxSymbol} ${roundedValue}`);
                    });
                } catch (e) {
                    // ignoruj nieprawidłowe ułamki
                }
            });
        });

        return results;
    }
    public getPolynomialRootsFromDividers(): number[] {
        const constantTerm = Math.abs(this.coefficients[this.coefficients.length - 1]);
        const dividers = new Dividers(constantTerm);
        const dividersWithMinuses = dividers.getDividersWithMinuses();

        return dividersWithMinuses.filter((x) => {
            const value = this.coefficients.reduce((acc, coef, index) => {
                const power = this.coefficients.length - 1 - index;
                return acc + coef * Math.pow(x, power);
            }, 0);
            return value === 0;
        });
    }
    public getPolynomialRationalRoots(): string[] {
        const numerators = this.getDividersA0(); // p
        const denominators = this.getDividersAn(); // q
        const roots = new Set<string>();

        numerators.forEach((p) => {
            denominators.forEach((q) => {
                try {
                    const positive = new Fraction(p, q);
                    const negative = new Fraction(-p, q);

                    [positive, negative].forEach((fraction) => {
                        const x = fraction.getValue();
                        const value = this.coefficients.reduce((acc, coef, index) => {
                            const power = this.coefficients.length - 1 - index;
                            return acc + coef * Math.pow(x, power);
                        }, 0);

                        if (Math.abs(value) < 1e-10) {
                            roots.add(fraction.getFractionReducedString());
                        }
                    });
                } catch (e) {
                    // Pomijamy dzielenie przez 0
                }
            });
        });

        return Array.from(roots);
    }



}

export default PolynomialFormula;
